import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Variation } from "@/hooks/useVariations";
import {
  VARIATION_NAME_MAX,
  normalizeVariationName,
  validateVariationName,
} from "@/lib/variationName";

interface RenameVariationModalProps {
  variation: Variation | null;
  existingNames: string[];
  isOpen: boolean;
  onClose: () => void;
  onRename: (variationId: string, newName: string) => void;
  isPending?: boolean;
}

export function RenameVariationModal({
  variation,
  existingNames,
  isOpen,
  onClose,
  onRename,
  isPending = false,
}: RenameVariationModalProps) {
  const [nameInput, setNameInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (variation && isOpen) {
      setNameInput(variation.name);
      setError(null);
    }
  }, [variation, isOpen]);

  const handleSubmit = () => {
    if (!variation) return;

    const fieldError = validateVariationName(nameInput);
    if (fieldError) {
      setError(fieldError);
      return;
    }
    const normalized = normalizeVariationName(nameInput);
    const otherNames = existingNames.filter(
      (n) => n.toLowerCase() !== variation.name.toLowerCase()
    );
    if (otherNames.some((n) => n.toLowerCase() === normalized.toLowerCase())) {
      setError(`A variation named "${normalized}" already exists`);
      return;
    }

    setError(null);
    onRename(variation.id, normalized);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isPending) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Rename Variation</DialogTitle>
          <DialogDescription>
            Update the display name for this variation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="variation-name">Variation name</Label>
            <Input
              id="variation-name"
              value={nameInput}
              onChange={(e) => {
                setNameInput(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder="e.g. US Promo"
              maxLength={VARIATION_NAME_MAX}
              autoFocus
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <p className="text-xs text-muted-foreground">
              Up to {VARIATION_NAME_MAX} characters. Letters, numbers, spaces, and{" "}
              <code className="font-mono">- _ . ( )</code> allowed.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!nameInput.trim() || isPending}
            className="bg-foreground text-primary-foreground hover:bg-accent hover:text-accent-foreground"
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
